import React, { useState, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { FormularioCampo } from '@/types/formulario';
import { useFormularioAreas } from '@/hooks/useFormularioAreas';
import { Calendar, Clock, FileUp, PenTool, Camera, MapPin, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface FormularioPreviewProps {
  campos: FormularioCampo[];
  formularioId?: string;
}

export const FormularioPreview = ({ campos, formularioId }: FormularioPreviewProps) => {
  const { areas } = useFormularioAreas(formularioId || '');
  
  // Estados de paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [camposPorPagina, setCamposPorPagina] = useState(10);

  // Ordenar campos por ordem para numeração global
  const camposOrdenados = useMemo(() => 
    [...campos].sort((a, b) => a.ordem - b.ordem), 
    [campos]
  );

  // Calcular paginação
  const usarPaginacao = camposOrdenados.length > 10;
  const totalPaginas = camposPorPagina === -1 ? 1 : Math.ceil(camposOrdenados.length / camposPorPagina);
  
  // Campos da página atual
  const camposPaginados = useMemo(() => {
    if (!usarPaginacao || camposPorPagina === -1) return camposOrdenados;
    const inicio = (currentPage - 1) * camposPorPagina;
    const fim = inicio + camposPorPagina;
    return camposOrdenados.slice(inicio, fim);
  }, [camposOrdenados, currentPage, camposPorPagina, usarPaginacao]);

  // Reset página ao mudar quantidade
  React.useEffect(() => {
    setCurrentPage(1);
  }, [camposPorPagina]);

  // Função para obter índice global de um campo
  const getGlobalIndex = React.useCallback((campoId: string) => {
    return camposOrdenados.findIndex(c => c.id === campoId) + 1;
  }, [camposOrdenados]);

  // Agrupar campos paginados por área
  const camposPorArea = useMemo(() => {
    const grupos: Record<string, FormularioCampo[]> = {
      'sem-area': [],
    };

    areas.forEach(area => {
      grupos[area.id] = [];
    });

    camposPaginados.forEach(campo => {
      if (campo.area_id && grupos[campo.area_id]) {
        grupos[campo.area_id].push(campo);
      } else {
        grupos['sem-area'].push(campo);
      }
    });

    return grupos;
  }, [camposPaginados, areas]);

  const renderField = (campo: FormularioCampo) => {
    const isRequired = campo.obrigatorio;
    const globalIndex = getGlobalIndex(campo.id);
    const numberedLabel = `${globalIndex} - ${campo.label}`;

    switch (campo.tipo_campo) {
      case 'texto':
        return (
          <div key={campo.id} className="space-y-2">
            <Label>
              {numberedLabel} {isRequired && <span className="text-destructive">*</span>}
            </Label>
            {campo.descricao && (
              <p className="text-sm text-muted-foreground">{campo.descricao}</p>
            )}
            <Input placeholder="Digite aqui..." disabled />
          </div>
        );

      case 'numero':
        return (
          <div key={campo.id} className="space-y-2">
            <Label>
              {numberedLabel} {isRequired && <span className="text-destructive">*</span>}
            </Label>
            {campo.descricao && (
              <p className="text-sm text-muted-foreground">{campo.descricao}</p>
            )}
            <Input type="number" placeholder="0" disabled />
          </div>
        );

      case 'data':
        return (
          <div key={campo.id} className="space-y-2">
            <Label>
              {numberedLabel} {isRequired && <span className="text-destructive">*</span>}
            </Label>
            {campo.descricao && (
              <p className="text-sm text-muted-foreground">{campo.descricao}</p>
            )}
            <div className="flex items-center gap-2 p-2 border rounded-md bg-muted">
              <Calendar className="h-4 w-4" />
              <span className="text-sm text-muted-foreground">Selecione uma data</span>
            </div>
          </div>
        );

      case 'hora':
        return (
          <div key={campo.id} className="space-y-2">
            <Label>
              {numberedLabel} {isRequired && <span className="text-destructive">*</span>}
            </Label>
            {campo.descricao && (
              <p className="text-sm text-muted-foreground">{campo.descricao}</p>
            )}
            <div className="flex items-center gap-2 p-2 border rounded-md bg-muted">
              <Clock className="h-4 w-4" />
              <span className="text-sm text-muted-foreground">Selecione um horário</span>
            </div>
          </div>
        );

      case 'textarea':
        return (
          <div key={campo.id} className="space-y-2">
            <Label>
              {numberedLabel} {isRequired && <span className="text-destructive">*</span>}
            </Label>
            {campo.descricao && (
              <p className="text-sm text-muted-foreground">{campo.descricao}</p>
            )}
            <Textarea placeholder="Digite aqui..." disabled rows={4} />
          </div>
        );

      case 'select':
        return (
          <div key={campo.id} className="space-y-2">
            <Label>
              {numberedLabel} {isRequired && <span className="text-destructive">*</span>}
            </Label>
            {campo.descricao && (
              <p className="text-sm text-muted-foreground">{campo.descricao}</p>
            )}
            <Select disabled>
              <SelectTrigger>
                <SelectValue placeholder="Selecione uma opção" />
              </SelectTrigger>
              <SelectContent>
                {campo.configuracoes?.opcoes?.map((opcao: string, idx: number) => (
                  <SelectItem key={idx} value={opcao}>
                    {opcao}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 'radio':
        const layoutRadio = campo.configuracoes?.layout_opcoes || 'vertical';
        const isHorizontalRadio = layoutRadio === 'horizontal';
        
        return (
          <div key={campo.id} className="space-y-2">
            <Label>
              {numberedLabel} {isRequired && <span className="text-destructive">*</span>}
            </Label>
            {campo.descricao && (
              <p className="text-sm text-muted-foreground">{campo.descricao}</p>
            )}
            <RadioGroup 
              disabled
              className={cn(isHorizontalRadio && "flex flex-wrap gap-4")}
            >
              {campo.configuracoes?.opcoes?.map((opcao: string, idx: number) => (
                <div 
                  key={idx} 
                  className={cn(
                    "flex items-center space-x-2",
                    isHorizontalRadio && "flex-shrink-0"
                  )}
                >
                  <RadioGroupItem value={opcao} id={`${campo.id}-${idx}`} />
                  <Label htmlFor={`${campo.id}-${idx}`}>{opcao}</Label>
                </div>
              ))}
            </RadioGroup>
          </div>
        );

      case 'checkbox':
        // Se tem opções configuradas (ex: Sim/Não), renderizar como radio
        if (campo.configuracoes?.opcoes && campo.configuracoes.opcoes.length > 0) {
          const layoutOpcoes = campo.configuracoes?.layout_opcoes || 'horizontal';
          const isHorizontal = layoutOpcoes === 'horizontal';
          
          return (
            <div key={campo.id} className="space-y-2">
              <Label>
                {numberedLabel} {isRequired && <span className="text-destructive">*</span>}
              </Label>
              {campo.descricao && (
                <p className="text-sm text-muted-foreground">{campo.descricao}</p>
              )}
              <RadioGroup 
                disabled
                className={cn(isHorizontal && "flex flex-wrap gap-4")}
              >
                {campo.configuracoes.opcoes.map((opcao: string, idx: number) => (
                  <div 
                    key={idx} 
                    className={cn(
                      "flex items-center space-x-2",
                      isHorizontal && "flex-shrink-0"
                    )}
                  >
                    <RadioGroupItem value={opcao} id={`${campo.id}-${idx}`} />
                    <Label htmlFor={`${campo.id}-${idx}`}>{opcao}</Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
          );
        }
        // Checkbox simples (sem opções)
        return (
          <div key={campo.id} className="space-y-2">
            <div className="flex items-start space-x-2">
              <Checkbox id={campo.id} disabled />
              <div className="space-y-1 leading-none">
                <Label htmlFor={campo.id}>
                  {numberedLabel} {isRequired && <span className="text-destructive">*</span>}
                </Label>
                {campo.descricao && (
                  <p className="text-sm text-muted-foreground">{campo.descricao}</p>
                )}
              </div>
            </div>
          </div>
        );

      case 'arquivo':
        return (
          <div key={campo.id} className="space-y-2">
            <Label>
              {numberedLabel} {isRequired && <span className="text-destructive">*</span>}
            </Label>
            {campo.descricao && (
              <p className="text-sm text-muted-foreground">{campo.descricao}</p>
            )}
            <div className="flex items-center gap-2 p-4 border-2 border-dashed rounded-md bg-muted">
              <FileUp className="h-5 w-5" />
              <span className="text-sm text-muted-foreground">Clique para fazer upload</span>
            </div>
          </div>
        );

      case 'assinatura':
        return (
          <div key={campo.id} className="space-y-2">
            <Label>
              {numberedLabel} {isRequired && <span className="text-destructive">*</span>}
            </Label>
            {campo.descricao && (
              <p className="text-sm text-muted-foreground">{campo.descricao}</p>
            )}
            <div className="flex items-center justify-center gap-2 p-8 border-2 border-dashed rounded-md bg-muted">
              <PenTool className="h-5 w-5" />
              <span className="text-sm text-muted-foreground">Área de assinatura</span>
            </div>
          </div>
        );

      case 'foto':
        return (
          <div key={campo.id} className="space-y-2">
            <Label>
              {numberedLabel} {isRequired && <span className="text-destructive">*</span>}
            </Label>
            {campo.descricao && (
              <p className="text-sm text-muted-foreground">{campo.descricao}</p>
            )}
            <div className="flex items-center gap-2 p-4 border-2 border-dashed rounded-md bg-muted">
              <Camera className="h-5 w-5" />
              <span className="text-sm text-muted-foreground">Tirar foto ou fazer upload</span>
            </div>
          </div>
        );

      case 'localizacao':
        return (
          <div key={campo.id} className="space-y-2">
            <Label>
              {numberedLabel} {isRequired && <span className="text-destructive">*</span>}
            </Label>
            {campo.descricao && (
              <p className="text-sm text-muted-foreground">{campo.descricao}</p>
            )}
            <div className="flex items-center gap-2 p-4 border rounded-md bg-muted">
              <MapPin className="h-5 w-5" />
              <span className="text-sm text-muted-foreground">Capturar localização GPS</span>
            </div>
          </div>
        );

      case 'escala':
          const tipoEscala = campo.configuracoes?.tipo_escala || 'numerica';
          const valoresTexto = campo.configuracoes?.valores_texto || [];
          const minimo = campo.configuracoes?.minimo || 1;
          const maximo = campo.configuracoes?.maximo || 5;
          const rotulos = campo.configuracoes?.rotulos || {};
          const totalValores = tipoEscala === 'texto' ? valoresTexto.length : maximo - minimo + 1;
        const usarSlider = totalValores > 5;
        const valorPreview = Math.floor((minimo + maximo) / 2);
        const estiloVisualizacao = campo.configuracoes?.estilo_visualizacao || 'texto';
        const emojisConfig = campo.configuracoes?.emojis || {};
        const tipoGradiente = campo.configuracoes?.tipo_gradiente || 'automatico_vermelho_verde';
        const escalaInvertida = campo.configuracoes?.escala_invertida || false;
        
        // Gerar cores se necessário
        const gerarCoresAutomaticas = (direcao: 'vermelho_verde' | 'verde_vermelho'): Record<number, string> => {
          const cores: Record<number, string> = {};
          for (let i = 0; i < totalValores; i++) {
            const valor = minimo + i;
            const percent = i / Math.max(totalValores - 1, 1);
            let r, g, b;
            if (direcao === 'vermelho_verde') {
              if (percent < 0.5) {
                r = 220; g = Math.floor(59 + (187 - 59) * (percent * 2)); b = 38;
              } else {
                r = Math.floor(220 - (220 - 22) * ((percent - 0.5) * 2));
                g = Math.floor(187 + (163 - 187) * ((percent - 0.5) * 2));
                b = Math.floor(38 + (74 - 38) * ((percent - 0.5) * 2));
              }
            } else {
              const invertPercent = 1 - percent;
              if (invertPercent < 0.5) {
                r = 220; g = Math.floor(59 + (187 - 59) * (invertPercent * 2)); b = 38;
              } else {
                r = Math.floor(220 - (220 - 22) * ((invertPercent - 0.5) * 2));
                g = Math.floor(187 + (163 - 187) * ((invertPercent - 0.5) * 2));
                b = Math.floor(38 + (74 - 38) * ((invertPercent - 0.5) * 2));
              }
            }
            cores[valor] = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
          }
          return cores;
        };
        
        let coresCalculadas: Record<number, string> = {};
        if (estiloVisualizacao === 'cores' || estiloVisualizacao === 'texto_cor') {
          if (tipoGradiente === 'manual') {
            coresCalculadas = campo.configuracoes?.cores || {};
          } else {
            coresCalculadas = gerarCoresAutomaticas(
              tipoGradiente === 'automatico_vermelho_verde' ? 'vermelho_verde' : 'verde_vermelho'
            );
          }
          
          // Inverter cores se necessário
          if (escalaInvertida) {
            const coresInvertidas: Record<number, string> = {};
            Array.from({ length: totalValores }, (_, i) => {
              const valorOriginal = minimo + i;
              const valorInvertido = maximo - i;
              coresInvertidas[valorOriginal] = coresCalculadas[valorInvertido];
            });
            coresCalculadas = coresInvertidas;
          }
        }
        
        // Inverter emojis se necessário
        let emojisAplicados = { ...emojisConfig };
        if (escalaInvertida && (estiloVisualizacao === 'emojis' || estiloVisualizacao === 'texto_emoji')) {
          const emojisInvertidos: Record<string, string> = {};
          Array.from({ length: totalValores }, (_, i) => {
            const valorOriginal = minimo + i;
            const valorInvertido = maximo - i;
            emojisInvertidos[valorOriginal] = emojisConfig[valorInvertido] || '';
          });
          emojisAplicados = emojisInvertidos;
        }
        
        return (
          <div key={campo.id} className="space-y-2">
            <Label>
              {numberedLabel} {isRequired && <span className="text-destructive">*</span>}
            </Label>
            {campo.descricao && (
              <p className="text-sm text-muted-foreground">{campo.descricao}</p>
            )}
            <div className="space-y-4">
              {usarSlider ? (
                // Slider para escalas grandes (mais de 5 valores)
                <>
                  <div className="py-4">
                    <Slider
                      min={minimo}
                      max={maximo}
                      step={1}
                      value={[valorPreview]}
                      disabled
                      className="w-full"
                    />
                  </div>
                  
                  {/* Rótulos das extremidades */}
                  <div className="flex justify-between text-xs sm:text-sm text-muted-foreground px-1">
                    <div className="text-left">
                      <div className="font-medium">{minimo}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm sm:text-base font-semibold text-primary">{valorPreview}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium">{maximo}</div>
                    </div>
                  </div>
                </>
              ) : (
                // Botões para escalas pequenas (5 ou menos valores)
                <>
                  <div className={cn(
                    "flex gap-3 sm:gap-2 px-1 sm:px-0",
                    // Layout adaptativo baseado em quantidade de valores
                    totalValores > 3 && totalValores <= 5 
                      ? "grid grid-cols-2 sm:flex sm:flex-row gap-3" 
                      : totalValores > 5 
                        ? "flex-col sm:flex-row" 
                        : "flex-row justify-between"
                  )}>
            {Array.from({ length: totalValores }, (_, i) => {
              const chave = tipoEscala === 'texto' ? i : minimo + i;
              const emoji = emojisAplicados[chave];
              const cor = coresCalculadas[chave];
              const rotulo = tipoEscala === 'texto' ? valoresTexto[i] : (rotulos[chave] || chave);
                      const isPreview = chave === valorPreview;
                      
                      const buttonStyle: React.CSSProperties = {};
                      if (estiloVisualizacao === 'cores' || estiloVisualizacao === 'texto_cor') {
                        buttonStyle.backgroundColor = isPreview ? cor : `${cor}40`;
                        buttonStyle.borderColor = cor;
                        buttonStyle.color = isPreview ? '#ffffff' : '#1f2937';
                      }
                      
                      return (
                        <Button
                          key={chave}
                          variant={isPreview && (estiloVisualizacao !== 'cores' && estiloVisualizacao !== 'texto_cor') ? "default" : "outline"}
                          size="lg"
                          disabled
                          className={cn(
                            "h-14 sm:h-16 min-w-[60px] sm:min-w-0 px-3 sm:px-4 flex flex-col gap-1 transition-all touch-manipulation",
                            totalValores <= 3 ? "sm:flex-1" : "flex-1",
                            isPreview && "ring-2 ring-primary shadow-lg sm:scale-105",
                          )}
                          style={buttonStyle}
                        >
                          {estiloVisualizacao === 'emojis' && (
                            <span className="text-lg sm:text-xl">{emoji || '❓'}</span>
                          )}
                          
                          {estiloVisualizacao === 'texto' && (
                            <span className="text-sm sm:text-base font-medium text-center">{rotulo}</span>
                          )}
                          
                          {estiloVisualizacao === 'texto_emoji' && (
                            <>
                              <span className="text-base sm:text-lg">{emoji || '❓'}</span>
                              <span className="text-[10px] sm:text-xs font-medium text-center leading-tight line-clamp-2">{rotulo}</span>
                            </>
                          )}
                          
                          {(estiloVisualizacao === 'cores' || estiloVisualizacao === 'texto_cor') && (
                            <span className="text-sm sm:text-base font-medium text-center line-clamp-2">{rotulo}</span>
                          )}
                        </Button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Card className="p-6">
      <h3 className="text-xl font-semibold mb-4">Pré-visualização do Formulário</h3>
      
      {/* Controles de paginação - topo */}
      {usarPaginacao && (
        <div className="flex flex-wrap items-center justify-between gap-4 p-3 mb-4 border rounded-lg bg-muted/50">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Campos por página:</span>
            <Select value={String(camposPorPagina)} onValueChange={(v) => setCamposPorPagina(Number(v))}>
              <SelectTrigger className="w-20 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5</SelectItem>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="15">15</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="-1">Todos</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {camposPorPagina !== -1 && (
            <>
              <span className="text-sm text-muted-foreground">
                Página {currentPage} de {totalPaginas} ({camposOrdenados.length} campos)
              </span>
              
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => setCurrentPage(p => p - 1)} 
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" /> Anterior
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => setCurrentPage(p => p + 1)} 
                  disabled={currentPage === totalPaginas}
                >
                  Próxima <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </>
          )}
        </div>
      )}
      
      <div className="space-y-6">
        {campos.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">
            Nenhum campo adicionado ainda. Adicione campos na paleta à esquerda.
          </p>
        ) : (
          <>
            {/* Áreas */}
            {areas.length > 0 && (
              <Accordion type="multiple" defaultValue={areas.filter(a => a.expandida_por_padrao).map(a => a.id)}>
                {areas.map((area) => {
                  const camposArea = camposPorArea[area.id] || [];
                  if (camposArea.length === 0) return null;

                  if (!area.expansivel) {
                    return (
                      <Card key={area.id} className="p-4 mb-4">
                        <h4 className="font-semibold mb-4">{area.titulo}</h4>
                        {area.descricao && (
                          <p className="text-sm text-muted-foreground mb-4">{area.descricao}</p>
                        )}
                        <div className="space-y-4">
                          {camposArea.sort((a, b) => a.ordem - b.ordem).map(renderField)}
                        </div>
                      </Card>
                    );
                  }

                  return (
                    <AccordionItem key={area.id} value={area.id}>
                      <AccordionTrigger className="text-lg font-semibold">
                        {area.titulo}
                      </AccordionTrigger>
                      <AccordionContent>
                        {area.descricao && (
                          <p className="text-sm text-muted-foreground mb-4">{area.descricao}</p>
                        )}
                        <div className="space-y-4 pt-2">
                          {camposArea.sort((a, b) => a.ordem - b.ordem).map(renderField)}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            )}

            {/* Campos sem área */}
            {camposPorArea['sem-area'].length > 0 && (
              <div className="space-y-4">
                {areas.length > 0 && (
                  <h4 className="font-semibold text-muted-foreground">Outros campos</h4>
                )}
                {camposPorArea['sem-area'].sort((a, b) => a.ordem - b.ordem).map(renderField)}
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Controles de paginação - rodapé */}
      {usarPaginacao && camposPorPagina !== -1 && (
        <div className="flex items-center justify-between gap-4 pt-4 mt-4 border-t">
          <span className="text-sm text-muted-foreground">
            Mostrando {((currentPage - 1) * camposPorPagina) + 1} - {Math.min(currentPage * camposPorPagina, camposOrdenados.length)} de {camposOrdenados.length}
          </span>
          
          <div className="flex gap-2">
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => setCurrentPage(p => p - 1)} 
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" /> Anterior
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => setCurrentPage(p => p + 1)} 
              disabled={currentPage === totalPaginas}
            >
              Próxima <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};
